import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "../lib/api";
import SEO from "../components/SEO";
import { useTheme } from "../context/ThemeContext";
import { Reveal } from "../components/motion/Reveal";
import ProductCard from "../components/ProductCard";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";

export default function Shop() {
  const { seo } = useTheme();
  const [params, setParams] = useSearchParams();
  const { slug: routeCategory } = useParams();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  const category = routeCategory || params.get("category") || "";
  const searching = !!params.get("q");
  const sort = params.get("sort") || (searching ? "relevance" : "newest");
  const availability = params.get("availability") || "";
  const saleOn = params.get("sale") === "1";
  const page = parseInt(params.get("page") || "1");
  const activeFilterCount = (category ? 1 : 0) + (availability ? 1 : 0) + (saleOn ? 1 : 0)
    + ((params.get("min_price") || params.get("max_price")) ? 1 : 0) + (searching ? 1 : 0);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const query = new URLSearchParams();
    if (params.get("q")) query.set("q", params.get("q"));
    if (category) query.set("category", category);
    if (sort) query.set("sort", sort);
    if (availability) query.set("availability", availability);
    if (params.get("sale")) query.set("sale", "1");
    if (params.get("min_price")) query.set("min_price", params.get("min_price"));
    if (params.get("max_price")) query.set("max_price", params.get("max_price"));
    query.set("page", page);
    api.get(`/products?${query}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [params, category, sort, availability, page]);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}).finally(() => setCategoriesLoaded(true)); }, []);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const update = (key, value, closeSheet = false) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next);
    if (closeSheet) setFiltersOpen(false);
  };

  const clearAll = () => {
    setQ("");
    setMinPrice("");
    setMaxPrice("");
    setParams(new URLSearchParams());
    setFiltersOpen(false);
  };

  const applyPrice = (e, closeSheet = false) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (minPrice) next.set("min_price", minPrice); else next.delete("min_price");
    if (maxPrice) next.set("max_price", maxPrice); else next.delete("max_price");
    next.delete("page");
    setParams(next);
    if (closeSheet) setFiltersOpen(false);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    update("q", q.trim());
  };

  // Active category derived from the already-fetched categories list
  const activeCategory = category ? categories.find((c) => c.slug === category) : null;

  // Route slug present + categories loaded + no match -> category not found
  if (routeCategory && categoriesLoaded && !activeCategory) {
    return (
      <div className="text-center py-32" data-testid="category-not-found">
        <SEO title="Category not found" noindex />
        <h1 className="font-serif text-3xl">Category not found</h1>
        <Link to="/shop" className="small-caps text-gold border-b border-gold mt-4 inline-block">Back to Shop</Link>
      </div>
    );
  }

  const emptyResults = !loading && data.total === 0;
  const seoTitle = activeCategory ? (activeCategory.seo_title || activeCategory.name) : (seo.shop_title || "Shop");
  const seoDescription = activeCategory
    ? (activeCategory.seo_description || `Shop ${activeCategory.name} — handcrafted metal home décor, manufacturer direct from Delhi.`)
    : (seo.shop_description || "Browse premium metal home décor — candle holders, bowls, trays, vases and more. Manufacturer direct from Delhi.");
  const collectionSchema = activeCategory && data.items.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: activeCategory.name,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: data.items.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: window.location.origin + "/product/" + p.slug,
        name: p.name,
      })),
    },
  } : undefined;

  const filterContent = (mobile = false) => (
    <>
      {!mobile && (
        <form onSubmit={submitSearch} className="flex items-center border-b border-black/20 focus-within:border-gold transition-colors duration-300">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." className="bg-transparent text-sm py-2 w-full outline-none" data-testid="shop-search-input" />
          <button type="submit" aria-label="Search" data-testid="shop-search-button"><Search strokeWidth={1.5} size={16} /></button>
        </form>
      )}

      <div>
        <p className="small-caps text-muted-foreground mb-4">Category</p>
        <button onClick={() => update("category", "", mobile)} className={`block py-1.5 text-sm transition-colors duration-300 ${!category ? "text-gold" : "hover:text-gold"}`} data-testid="filter-category-all">All Categories</button>
        {categories.map((c) => (
          <button key={c.id} onClick={() => update("category", c.slug, mobile)} className={`block py-1.5 text-sm transition-colors duration-300 ${category === c.slug ? "text-gold" : "hover:text-gold"}`} data-testid={`filter-category-${c.slug}`}>
            {c.name}
          </button>
        ))}
      </div>

      <form onSubmit={(e) => applyPrice(e, mobile)}>
        <p className="small-caps text-muted-foreground mb-4">Price (₹)</p>
        <div className="flex items-center gap-2">
          <Input value={minPrice} onChange={(e) => setMinPrice(e.target.value)} placeholder="Min" type="number" className="bg-white h-9 text-sm" data-testid="filter-min-price" />
          <span className="text-muted-foreground">—</span>
          <Input value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max" type="number" className="bg-white h-9 text-sm" data-testid="filter-max-price" />
        </div>
        <button type="submit" className="mt-3 small-caps border-b border-black hover:border-gold hover:text-gold transition-colors duration-300" data-testid="filter-price-apply">Apply</button>
      </form>

      <div>
        <p className="small-caps text-muted-foreground mb-4">Availability</p>
        <button onClick={() => update("availability", "", mobile)} className={`block py-1.5 text-sm ${!availability ? "text-gold" : "hover:text-gold"} transition-colors duration-300`} data-testid="filter-avail-all">All</button>
        <button onClick={() => update("availability", "in_stock", mobile)} className={`block py-1.5 text-sm ${availability === "in_stock" ? "text-gold" : "hover:text-gold"} transition-colors duration-300`} data-testid="filter-avail-instock">In Stock</button>
        <button onClick={() => update("sale", saleOn ? "" : "1", mobile)} className={`block py-1.5 text-sm ${saleOn ? "text-gold" : "hover:text-gold"} transition-colors duration-300`} data-testid="filter-sale">On Sale</button>
      </div>

      {activeFilterCount > 0 && (
        <button onClick={clearAll} className="small-caps text-destructive border-b border-destructive self-start hover:opacity-70 transition-opacity duration-300" data-testid="clear-filters">
          Clear All Filters
        </button>
      )}
    </>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="shop-page">
      <SEO title={seoTitle} description={seoDescription} schema={emptyResults ? undefined : collectionSchema} />
      <Reveal>
        <p className="small-caps text-gold mb-3" data-eid="shop-kicker" data-etype="text">The Collection</p>
        <h1 className="text-4xl md:text-5xl tracking-tighter font-semibold mb-10" data-eid="shop-heading" data-etype="text">{activeCategory ? activeCategory.name : "Shop All Décor"}</h1>
      </Reveal>

      <div className="md:hidden mb-6 flex items-center gap-4">
        <form onSubmit={submitSearch} className="flex flex-1 items-center border-b border-black/20 focus-within:border-gold transition-colors duration-300">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." className="bg-transparent text-sm py-2 w-full outline-none" data-testid="shop-search-input-mobile" />
          <button type="submit" aria-label="Search" data-testid="shop-search-button-mobile"><Search strokeWidth={1.5} size={16} /></button>
        </form>
        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetTrigger asChild>
            <button className="flex items-center gap-2 border border-black/20 px-4 py-2 small-caps hover:border-gold hover:text-gold transition-colors duration-300" data-testid="mobile-filters-button">
              <SlidersHorizontal strokeWidth={1.5} size={14} /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-[#FAF9F6] w-[300px]" data-testid="mobile-filters-sheet">
            <SheetHeader>
              <SheetTitle className="font-serif text-xl">Filters</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col gap-8 mt-6 overflow-y-auto">{filterContent(true)}</div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-12">
        <aside className="hidden md:flex flex-col gap-8" data-testid="filters-sidebar">
          {filterContent(false)}
        </aside>

        <div>
          <div className="flex items-center justify-between mb-8">
            <p className="text-sm text-muted-foreground" data-testid="results-count">{data.total} products</p>
            <Select value={sort} onValueChange={(v) => update("sort", v)}>
              <SelectTrigger className="w-44 bg-white rounded-sm" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {searching && <SelectItem value="relevance" data-testid="sort-relevance">Relevance</SelectItem>}
                <SelectItem value="newest" data-testid="sort-newest">Newest</SelectItem>
                <SelectItem value="price_low" data-testid="sort-price-low">Price: Low to High</SelectItem>
                <SelectItem value="price_high" data-testid="sort-price-high">Price: High to Low</SelectItem>
                <SelectItem value="name" data-testid="sort-name">Name A–Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-8">{[...Array(6)].map((_, i) => <div key={i} className="aspect-square bg-muted animate-pulse" />)}</div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-24" data-testid="empty-results">
              <p className="font-serif text-2xl">No products found</p>
              <p className="text-sm text-muted-foreground mt-2">
                {searching ? `Nothing matches “${params.get("q")}”. Try a different word, or browse the full collection.` : "Try a different search or filter."}
              </p>
              {activeFilterCount > 0 && (
                <button onClick={clearAll} className="mt-6 border border-black px-8 py-3 small-caps hover:bg-[#111] hover:text-[#FAF9F6] transition-colors duration-300" data-testid="empty-clear-filters">
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
              {data.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}

          {data.pages > 1 && (
            <div className="flex justify-center gap-2 mt-16" data-testid="pagination">
              {[...Array(data.pages)].map((_, i) => (
                <button key={i} onClick={() => update("page", String(i + 1))}
                  className={`w-10 h-10 text-sm border transition-colors duration-300 ${page === i + 1 ? "bg-[#111] text-[#FAF9F6] border-[#111]" : "border-black/20 hover:border-gold"}`}
                  data-testid={`page-${i + 1}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
